/**
 * Piezas de interfaz compartidas, portadas de la v1.
 *
 * Los tipos de dominio vienen de `@compranorte/domain`: los colores de estado
 * y los pasos de la barra de progreso se indexan por el enum real, así que si
 * el dominio gana un estado esto deja de compilar en vez de pintar un chip sin
 * color.
 */
import type { ReactNode } from 'react'
import type { EstadoOrden, ModoDespacho } from '@compranorte/domain'
import { money as fmtMoney } from '../lib/format'
import { IconoAlerta, IconoCamion, IconoCaja } from './icons'

export function Money({
  v,
  className = '',
  signo = false,
}: {
  v: number
  className?: string
  signo?: boolean
}) {
  const negativo = v < 0
  return (
    <span
      className={`num tabular-nums ${negativo ? 'text-error-500' : ''} ${className}`}
      title={fmtMoney(v)}
    >
      {signo && v > 0 ? '+' : ''}
      {fmtMoney(v)}
    </span>
  )
}

export function Panel({
  titulo,
  acciones,
  children,
  className = '',
  padding = true,
}: {
  titulo?: ReactNode
  acciones?: ReactNode
  children: ReactNode
  className?: string
  padding?: boolean
}) {
  return (
    <section className={`panel ${className}`}>
      {(titulo || acciones) && (
        <header className="panel-head">
          {titulo ? <h2 className="panel-title">{titulo}</h2> : <span />}
          {acciones}
        </header>
      )}
      <div className={padding ? 'p-3' : ''}>{children}</div>
    </section>
  )
}

const COLORES_ESTADO: Record<EstadoOrden, string> = {
  recibida: 'bg-acero-100 text-acero-700 border-acero-300',
  confirmada: 'bg-marca-700/10 text-marca-700 border-marca-700/30',
  en_preparación: 'bg-senal-100 text-senal-700 border-senal-500/50',
  despachada: 'bg-marca-400/15 text-marca-600 border-marca-400/40',
  recibido_en_bodega: 'bg-marca-400/25 text-marca-700 border-marca-400/50',
  entregada: 'bg-ok-100 text-ok-500 border-ok-500/30',
  cancelada: 'bg-error-100 text-error-500 border-error-500/40',
}

/**
 * Cómo se le nombra cada estado a una persona.
 *
 * `estado.replace('_', ' ')` alcanzaba mientras el único estado compuesto era
 * `en_preparación`: con `recibido_en_bodega` habría quedado "recibido en_bodega",
 * porque `replace` sin bandera global cambia SOLO la primera coincidencia. Un
 * mapa explícito además permite decir "en bodega" en vez del identificador.
 */
const ETIQUETA_ESTADO: Record<EstadoOrden, string> = {
  recibida: 'recibida',
  confirmada: 'confirmada',
  'en_preparación': 'en preparación',
  despachada: 'despachada',
  recibido_en_bodega: 'en bodega',
  entregada: 'entregada',
  cancelada: 'cancelada',
}

export function ChipEstado({ estado }: { estado: EstadoOrden }) {
  return <span className={`chip ${COLORES_ESTADO[estado]}`}>{ETIQUETA_ESTADO[estado]}</span>
}

export function ChipDespacho({ modo }: { modo: ModoDespacho }) {
  return modo === 'consolidado' ? (
    <span className="chip bg-marca-700 text-white border-marca-700">
      <IconoCamion size={12} /> consolidado
    </span>
  ) : (
    <span className="chip bg-white text-acero-600 border-acero-300">
      <IconoCaja size={12} /> directo
    </span>
  )
}

export function Dato({
  etiqueta,
  valor,
  className = '',
}: {
  etiqueta: string
  valor: ReactNode
  className?: string
}) {
  return (
    <div className={className}>
      <div className="text-[10.5px] uppercase tracking-[0.08em] text-acero-500 font-semibold">
        {etiqueta}
      </div>
      <div className="text-[13px] text-acero-900 mt-0.5">{valor}</div>
    </div>
  )
}

export function Metrica({
  etiqueta,
  valor,
  detalle,
  acento = 'neutro',
}: {
  etiqueta: string
  valor: ReactNode
  detalle?: ReactNode
  acento?: 'neutro' | 'positivo' | 'negativo' | 'marca'
}) {
  const color =
    acento === 'positivo'
      ? 'text-ok-500'
      : acento === 'negativo'
        ? 'text-error-500'
        : acento === 'marca'
          ? 'text-cobre-500'
          : 'text-acero-900'
  return (
    <div className="border border-acero-200 bg-white px-3 py-2.5 rounded-sm">
      <div className="text-[10.5px] uppercase tracking-[0.08em] text-acero-500 font-semibold">
        {etiqueta}
      </div>
      <div className={`num text-[19px] font-semibold mt-1 leading-none ${color}`}>{valor}</div>
      {detalle && <div className="text-[11px] text-acero-500 mt-1.5 leading-snug">{detalle}</div>}
    </div>
  )
}

export function Vacio({
  titulo,
  detalle,
  accion,
}: {
  titulo: string
  detalle: string
  accion?: ReactNode
}) {
  return (
    <div className="text-center py-10 px-4 border border-dashed border-acero-300 bg-acero-50/60 rounded-sm">
      <div className="mx-auto w-9 h-9 grid place-items-center rounded-sm bg-acero-200 text-acero-600">
        <IconoCaja size={18} />
      </div>
      <h3 className="mt-3 text-[14px] font-semibold text-acero-800">{titulo}</h3>
      <p className="mt-1 text-[12.5px] text-acero-600 max-w-md mx-auto leading-relaxed">{detalle}</p>
      {accion && <div className="mt-3">{accion}</div>}
    </div>
  )
}

export function Aviso({
  tono = 'info',
  titulo,
  children,
}: {
  tono?: 'info' | 'alerta' | 'error' | 'ok'
  titulo?: string
  children: ReactNode
}) {
  const estilos = {
    info: 'bg-marca-700/5 border-marca-700/25 text-acero-800',
    alerta: 'bg-senal-100 border-senal-500/50 text-acero-800',
    error: 'bg-error-100 border-error-500/40 text-acero-900',
    ok: 'bg-ok-100 border-ok-500/30 text-acero-800',
  }[tono]
  const iconoColor = {
    info: 'text-marca-600',
    alerta: 'text-senal-700',
    error: 'text-error-500',
    ok: 'text-ok-500',
  }[tono]
  return (
    <div className={`border px-3 py-2 rounded-sm flex gap-2 items-start ${estilos}`}>
      <IconoAlerta size={15} className={`mt-0.5 shrink-0 ${iconoColor}`} />
      <div className="text-[12.5px] leading-relaxed">
        {titulo && <div className="font-semibold mb-0.5">{titulo}</div>}
        {children}
      </div>
    </div>
  )
}

export function Campo({
  etiqueta,
  error,
  ayuda,
  children,
  requerido = false,
}: {
  etiqueta: string
  error?: string
  ayuda?: string
  children: ReactNode
  requerido?: boolean
}) {
  return (
    <label className="block">
      <span className="etiqueta">
        {etiqueta}
        {requerido && <span className="text-error-500 ml-0.5">*</span>}
      </span>
      {children}
      {error ? (
        <p className="error-msg">{error}</p>
      ) : ayuda ? (
        <p className="ayuda">{ayuda}</p>
      ) : null}
    </label>
  )
}

export function BarraProgreso({ estado }: { estado: EstadoOrden }) {
  // Una orden cancelada no está "a mitad de camino": no tiene avance.
  if (estado === 'cancelada') {
    return <span className="chip bg-error-100 text-error-500 border-error-500/40">cancelada</span>
  }

  /*
   * `recibido_en_bodega` se intercala SOLO si la orden está ahí.
   *
   * No es un paso de la secuencia: el despacho directo no pasa por bodega, y
   * dibujar siempre seis casillas prometería una etapa que la mitad de las
   * órdenes nunca tiene. Cuando la orden sí está en bodega, aparece en su lugar
   * —entre despachada y entregada— y la barra tiene un paso más.
   */
  const pasos: EstadoOrden[] = [
    'recibida',
    'confirmada',
    'en_preparación',
    'despachada',
    ...(estado === 'recibido_en_bodega' ? (['recibido_en_bodega'] as const) : []),
    'entregada',
  ]
  const actual = pasos.indexOf(estado)
  return (
    <ol className="flex items-center gap-1 min-w-0">
      {pasos.map((p, i) => (
        <li key={p} className="flex items-center gap-1 min-w-0">
          <span
            className={`h-1.5 w-6 sm:w-9 rounded-sm ${
              i <= actual ? 'bg-marca-600' : 'bg-acero-200'
            }`}
            title={ETIQUETA_ESTADO[p]}
          />
          {i === pasos.length - 1 && (
            <span className="ml-1.5 text-[11px] text-acero-600 whitespace-nowrap">
              {ETIQUETA_ESTADO[estado]}
            </span>
          )}
        </li>
      ))}
    </ol>
  )
}
